/**
 * New Conversation Modal Component
 * Allows users to start new direct or group conversations
 */

import React, { useState, useEffect } from 'react';
import { X, Search, User, Users } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { messagingService } from '@/firebase/messagingService';
import { Conversation } from '@/types/messaging';
import { userService } from '@/firebase/database';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/firebase/config';

interface NewConversationModalProps {
  onClose: () => void;
  onConversationCreated: (conversation: Conversation) => void;
  canCreateGroup: boolean;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
  picture?: string;
  /** Org IDs this user belongs to (for computing shared org for direct chat) */
  organizationIds: string[];
  /** Display name of first shared org (user and current user both belong) */
  organizationName?: string;
}

const NewConversationModal: React.FC<NewConversationModalProps> = ({
  onClose,
  onConversationCreated,
  canCreateGroup,
}) => {
  const { user } = useAuth();
  const { currentOrganization, organizations } = useOrganization();
  const [mode, setMode] = useState<'direct' | 'group'>('direct');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<UserOption[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [organizationFilter, setOrganizationFilter] = useState<string | null>(null); // null = "All"
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const myOrgIds: string[] = organizations?.map((o) => o.id).filter((id): id is string => id != null) ?? [];
  const orgIdToName = new Map<string, string>();
  organizations?.forEach((o) => {
    if (o.id && o.name) orgIdToName.set(o.id, o.name);
  });

  // Load users from all organizations (common messages screen)
  useEffect(() => {
    const loadUsers = async () => {
      if (!myOrgIds.length || !user?.id) {
        setUsers([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const usersRef = collection(db, 'users');
        const userMap = new Map<string, UserOption>();

        for (const orgId of myOrgIds) {
          const q = query(
            usersRef,
            where('organizationIds', 'array-contains', orgId)
          );
          const snapshot = await getDocs(q);
          snapshot.forEach((docSnap) => {
            if (docSnap.id === user?.id) return;
            const data = docSnap.data();
            const userOrgIds: string[] = Array.isArray(data.organizationIds)
              ? data.organizationIds
              : data.organizationId
                ? [data.organizationId]
                : [];
            const sharedOrgIds = myOrgIds.filter((id) => userOrgIds.includes(id));
            const firstShared = sharedOrgIds[0];
            const organizationName = firstShared != null ? orgIdToName.get(firstShared) : undefined;
            const existing = userMap.get(docSnap.id);
            if (!existing) {
              userMap.set(docSnap.id, {
                id: docSnap.id,
                name: data.name || data.email || 'Unknown User',
                email: data.email || '',
                picture: data.picture,
                organizationIds: userOrgIds,
                organizationName,
              });
            }
          });

          const orgIdOnlyQuery = query(usersRef, where('organizationId', '==', orgId));
          const orgIdSnapshot = await getDocs(orgIdOnlyQuery);
          orgIdSnapshot.forEach((docSnap) => {
            if (docSnap.id === user?.id) return;
            const data = docSnap.data();
            const existing = userMap.get(docSnap.id);
            if (!existing) {
              const userOrgIds: string[] = Array.isArray(data.organizationIds)
                ? data.organizationIds
                : data.organizationId
                  ? [data.organizationId]
                  : [];
              const sharedOrgIds = myOrgIds.filter((id) => userOrgIds.includes(id));
              const firstShared = sharedOrgIds[0];
              const organizationName = firstShared != null ? orgIdToName.get(firstShared) : undefined;
              userMap.set(docSnap.id, {
                id: docSnap.id,
                name: data.name || data.email || 'Unknown User',
                email: data.email || '',
                picture: data.picture,
                organizationIds: userOrgIds,
                organizationName,
              });
            }
          });
        }

        const uniqueUsers = Array.from(userMap.values()).sort((a, b) => a.name.localeCompare(b.name));
        setUsers(uniqueUsers);
      } catch (error: any) {
        console.error('Error loading users:', error);
        setError('Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [user?.id, myOrgIds.join(',')]);

  // Filter users by organization and search query
  useEffect(() => {
    let list = users;

    if (organizationFilter) {
      list = list.filter((u) => u.organizationIds.includes(organizationFilter));
    }

    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      list = list.filter(
        (u) =>
          u.name.toLowerCase().includes(searchLower) ||
          u.email.toLowerCase().includes(searchLower) ||
          (u.organizationName && u.organizationName.toLowerCase().includes(searchLower))
      );
    }

    setFilteredUsers(list);
  }, [searchQuery, users, organizationFilter]);

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleCreateConversation = async () => {
    if (!user?.id) {
      setError('Missing user information');
      return;
    }

    setError('');
    setCreating(true);

    try {
      if (mode === 'direct') {
        if (selectedUsers.length !== 1) {
          setError('Please select exactly one user for direct message');
          setCreating(false);
          return;
        }

        const selectedUser = users.find((u) => u.id === selectedUsers[0]);
        const sharedOrgIds =
          selectedUser != null
            ? myOrgIds.filter((id) => selectedUser.organizationIds.includes(id))
            : [];
        const directOrgId = sharedOrgIds[0] ?? currentOrganization?.id ?? organizations?.[0]?.id;
        if (!directOrgId) {
          setError('No shared organization with this user');
          setCreating(false);
          return;
        }

        const result = await messagingService.createDirectConversation(
          user.id,
          selectedUsers[0],
          directOrgId
        );

        if (result.success && result.data) {
          onConversationCreated(result.data);
          onClose();
        } else {
          setError(result.error || 'Failed to create conversation');
        }
      } else {
        const groupOrgId = currentOrganization?.id ?? organizations?.[0]?.id;
        if (!groupOrgId) {
          setError('Please select an organization for the group');
          setCreating(false);
          return;
        }
        if (!groupName.trim()) {
          setError('Group name is required');
          setCreating(false);
          return;
        }

        if (selectedUsers.length === 0) {
          setError('Please select at least one member for the group');
          setCreating(false);
          return;
        }

        const result = await messagingService.createGroupConversation(
          groupName.trim(),
          groupDescription.trim() || undefined,
          selectedUsers,
          groupOrgId,
          user.id
        );

        if (result.success && result.data) {
          onConversationCreated(result.data);
          onClose();
        } else {
          setError(result.error || 'Failed to create group');
        }
      }
    } catch (error: any) {
      console.error('Error creating conversation:', error);
      setError(error.message || 'Failed to create conversation');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            New Conversation
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Mode Toggle */}
        {canCreateGroup && (
          <div className="px-6 py-3 border-b border-gray-200 flex space-x-2">
            <button
              onClick={() => {
                setMode('direct');
                setSelectedUsers([]);
                setError('');
              }}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                mode === 'direct'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <User className="w-4 h-4 inline-block mr-2" />
              Direct Message
            </button>
            <button
              onClick={() => {
                setMode('group');
                setSelectedUsers([]);
                setError('');
              }}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                mode === 'group'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Users className="w-4 h-4 inline-block mr-2" />
              Group
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {mode === 'group' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Enter group name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  placeholder="Enter group description"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {mode === 'direct' ? 'Select User' : 'Select Members'}
            </label>
            {organizations && organizations.length > 1 && (
              <div className="mb-3">
                <label htmlFor="org-filter" className="block text-xs font-medium text-gray-500 mb-1">
                  Organization
                </label>
                <select
                  id="org-filter"
                  value={organizationFilter ?? ''}
                  onChange={(e) => setOrganizationFilter(e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-sm"
                >
                  <option value="">All organizations</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id ?? ''}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {loading ? (
              <p className="text-gray-500 text-sm">Loading users...</p>
            ) : filteredUsers.length === 0 ? (
              <p className="text-gray-500 text-sm">No users found</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredUsers.map((userOption) => (
                  <button
                    key={userOption.id}
                    onClick={() => toggleUserSelection(userOption.id)}
                    className={`w-full p-3 rounded-lg border transition-colors text-left ${
                      selectedUsers.includes(userOption.id)
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {userOption.picture ? (
                        <img
                          src={userOption.picture}
                          alt={userOption.name}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{userOption.name}</p>
                        <p className="text-sm text-gray-500 truncate">{userOption.email}</p>
                        {userOption.organizationName && !organizationFilter && (
                          <p className="text-xs text-gray-400 truncate">{userOption.organizationName}</p>
                        )}
                      </div>
                      {selectedUsers.includes(userOption.id) && (
                        <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateConversation}
            disabled={creating || (mode === 'direct' && selectedUsers.length !== 1) || (mode === 'group' && (!groupName.trim() || selectedUsers.length === 0))}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {creating ? 'Creating...' : mode === 'direct' ? 'Start Conversation' : 'Create Group'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewConversationModal;

